import { Tokens } from '@/assets/tokens'
import { Checkbox, SelectionOption, Selector } from '@/components/core'
import { Paginator } from '@/components/derived'
import { SkeletonLoader } from '@/components/derived/SkeletonLoader/SkeletonLoader.component'
import { AlphabeticalSorting, AlphabeticalSortingTwo, MenuFoldOne, SettingTwo } from '@icon-park/react'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import {
  ActionCell,
  ActionContainer,
  ContentContainer,
  ControlGroup,
  ExpansionContainer,
  ExpansionRow,
  Footer,
  HeaderCell,
  MainRow,
  PageSizeSelector,
  RowCell,
  SelectionContainer,
  SortButton,
  SortControl,
  StyledSelect,
  TableBody,
  TableContainer,
  TableHeader,
  TableRow,
  TooltipContainer,
  TopControls,
} from './FlexTable.styles'

export interface FlexTableColumnType<T> {
  key: string
  title?: string | ReactNode
  width?: number | string
  minWidth?: number
  align?: 'left' | 'center' | 'right'
  render?: (item: T, index: number) => ReactNode
}

export interface SortState {
  key: string
  direction: 'ASC' | 'DESC'
}

export interface SortOption {
  key: string
  label: string
}

export interface FlexTableProps<T> {
  data?: T[]
  columns: FlexTableColumnType<T>[]
  totalPages?: number
  isLoading?: boolean
  name?: string
  isScrollable?: boolean
  maxHeight?: string
  rowGap?: string
  headerHeight?: string
  rowHeight?: string
  renderRowTags?: (item: T, index: number) => ReactNode
  renderRowMetrics?: (item: T, index: number) => ReactNode
  enableSelection?: boolean
  selectedItems?: Set<any>
  onItemSelect?: (item: T) => void
  selectAllChecked?: boolean
  onSelectAll?: () => void
  getItemId?: (item: T) => any
  onSortChange?: (sortState: SortState) => void
  initialSort?: SortState
  currentSort?: SortState
  pageSizeOptions?: number[]
  onPageSizeChange?: (size: number) => void
  currentPageSize?: number
  sortOptions?: SortOption[]
  enableSorting?: boolean
}

export function FlexTable<T>({
  name,
  columns,
  data,
  totalPages = 1,
  isLoading = false,
  isScrollable = false,
  maxHeight = '400px',
  headerHeight = '60px',
  rowHeight = '70px',
  renderRowTags,
  renderRowMetrics,
  enableSelection = false,
  selectedItems,
  onItemSelect,
  selectAllChecked = false,
  onSelectAll,
  getItemId,
  onSortChange,
  initialSort = { key: 'date', direction: 'DESC' },
  currentSort,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
  currentPageSize,
  sortOptions,
  enableSorting = true,
}: FlexTableProps<T>) {
  const [internalSortState, setInternalSortState] = useState<SortState>(
    initialSort || { key: 'date', direction: 'DESC' },
  )
  const sortState = currentSort || internalSortState

  const [selectedSortOption, setSelectedSortOption] = useState<SelectionOption | undefined>(() => {
    if (sortOptions && sortState) {
      const matchingOption = sortOptions.find((option) => option.key === sortState.key)
      return matchingOption ? { value: matchingOption.key, text: matchingOption.label } : undefined
    }
    return sortOptions?.[0] ? { value: sortOptions[0].key, text: sortOptions[0].label } : undefined
  })

  useEffect(() => {
    if (currentSort && sortOptions) {
      const matchingOption = sortOptions.find((option) => option.key === currentSort.key)
      if (matchingOption) {
        setSelectedSortOption({ value: matchingOption.key, text: matchingOption.label })
      }
    }
  }, [currentSort, sortOptions])

  const handleSortOptionChange = useCallback(
    (option?: SelectionOption) => {
      if (!option) return

      setSelectedSortOption(option)
      const newSortState: SortState = { key: String(option.value), direction: sortState.direction }

      if (!currentSort) {
        setInternalSortState(newSortState)
      }

      onSortChange?.(newSortState)
    },
    [sortState.direction, onSortChange, currentSort],
  )

  const handleAscendingSort = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      const key = selectedSortOption?.value ? String(selectedSortOption.value) : sortOptions?.[0]?.key || 'date'
      const newSortState: SortState = { key, direction: 'ASC' }
      if (!currentSort) {
        setInternalSortState(newSortState)
      }

      onSortChange?.(newSortState)
    },
    [selectedSortOption, sortOptions, onSortChange, currentSort],
  )

  const handleDescendingSort = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      const key = selectedSortOption?.value ? String(selectedSortOption.value) : sortOptions?.[0]?.key || 'date'
      const newSortState: SortState = { key, direction: 'DESC' }

      if (!currentSort) {
        setInternalSortState(newSortState)
      }

      onSortChange?.(newSortState)
    },
    [selectedSortOption, sortOptions, onSortChange, currentSort],
  )

  const selectorOptions: SelectionOption[] =
    sortOptions?.map((option) => ({
      value: option.key,
      text: option.label,
    })) || []

  const shouldShowSortControls = enableSorting && sortOptions && sortOptions.length > 0

  if (!isLoading && (!data || data.length < 1)) {
    return (
      <TableContainer>
        <div style={{ padding: '40px', textAlign: 'center', color: Tokens.ColorTextTertiary }}>
          Nenhum dado disponível
        </div>
      </TableContainer>
    )
  }

  return (
    <TableContainer>
      {shouldShowSortControls && (
        <TopControls>
          <ControlGroup>
            <SortControl>
              <Selector.Icon value={MenuFoldOne} color={Tokens.ColorTextSecondary} />
              <div style={{ minWidth: '200px' }}>
                <Selector.Default
                  placeholder="Selecione ordenação"
                  options={selectorOptions}
                  selectedOption={selectedSortOption}
                  onSelect={handleSortOptionChange}
                  $fit={true}
                  disabled={isLoading}
                />
              </div>
            </SortControl>

            <SortControl>
              <TooltipContainer>
                <SortButton
                  $active={sortState.direction === 'ASC'}
                  onClick={handleAscendingSort}
                  type="button"
                  disabled={isLoading}
                  onMouseDown={(e) => e.preventDefault()}>
                  <AlphabeticalSorting size={16} />
                </SortButton>
                <div className="tooltip">Ordenação Crescente</div>
              </TooltipContainer>

              <TooltipContainer>
                <SortButton
                  $active={sortState.direction === 'DESC'}
                  onClick={handleDescendingSort}
                  type="button"
                  disabled={isLoading}
                  onMouseDown={(e) => e.preventDefault()}>
                  <AlphabeticalSortingTwo size={16} />
                </SortButton>
                <div className="tooltip">Ordenação Decrescente</div>
              </TooltipContainer>
            </SortControl>
          </ControlGroup>
        </TopControls>
      )}

      <TableHeader $height={headerHeight}>
        {enableSelection && (
          <SelectionContainer>
            <Checkbox
              checked={selectAllChecked}
              onChange={() => {
                onSelectAll?.()
              }}
              color="green"
              width={16}
              aria-label="Selecionar todos"
            />
          </SelectionContainer>
        )}
        {columns.map((column) => {
          if (column.key === 'actions') {
            return (
              <ActionContainer key={column.key} $width={column.width} $minWidth={column.minWidth}>
                <HeaderCell $align={column.align} $width="100%">
                  {column.title}
                </HeaderCell>
              </ActionContainer>
            )
          }

          return (
            <HeaderCell key={column.key} $width={column.width} $minWidth={column.minWidth} $align={column.align}>
              {column.title}
            </HeaderCell>
          )
        })}
      </TableHeader>

      {isLoading ?
        <SkeletonLoader amount={5} />
      : <TableBody style={{ maxHeight: isScrollable ? maxHeight : 'none', overflowY: 'auto' }}>
          {data?.map((item, index) => {
            const itemId = getItemId ? getItemId(item) : (item as any).id
            const isSelected = selectedItems?.has(itemId) || false
            const actionColumn = columns.find((col) => col.key === 'actions')
            const contentColumns = columns.filter((col) => col.key !== 'actions')

            return (
              <TableRow key={`row-${index}`} $isSelected={isSelected} $striped={true} $index={index}>
                {enableSelection && (
                  <SelectionContainer>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => {
                        onItemSelect?.(item)
                      }}
                      color="green"
                      width={16}
                      aria-label="Selecionar linha"
                    />
                  </SelectionContainer>
                )}

                <ContentContainer>
                  <MainRow $height={rowHeight}>
                    {contentColumns.map((column) => (
                      <RowCell
                        key={`cell-${column.key}-${index}`}
                        $width={column.width}
                        $minWidth={column.minWidth}
                        $align={column.align}>
                        {column.render ? column.render(item, index) : (item as any)[column.key]}
                      </RowCell>
                    ))}
                  </MainRow>

                  {(renderRowMetrics || renderRowTags) && (
                    <ExpansionContainer>
                      {renderRowTags && <ExpansionRow>{renderRowTags(item, index)}</ExpansionRow>}

                      {renderRowMetrics && <ExpansionRow>{renderRowMetrics(item, index)}</ExpansionRow>}
                    </ExpansionContainer>
                  )}
                </ContentContainer>

                {actionColumn && (
                  <ActionContainer $width={actionColumn.width} $minWidth={actionColumn.minWidth}>
                    <ActionCell>{actionColumn.render?.(item, index)}</ActionCell>
                  </ActionContainer>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      }

      {(totalPages > 1 || onPageSizeChange) && (
        <Footer>
          {onPageSizeChange && (
            <PageSizeSelector>
              <SettingTwo size={16} color={Tokens.ColorTextSecondary} />
              <span>Itens por página:</span>
              <StyledSelect
                value={currentPageSize || pageSizeOptions[0]}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                disabled={isLoading}>
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </StyledSelect>
            </PageSizeSelector>
          )}

          <Paginator totalPages={totalPages} name={name} />
        </Footer>
      )}
    </TableContainer>
  )
}
